import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { updateModuleCommand } from 'owa-publishedcalendar-settings';
import {
    CalendarSurfaceStore,
    SurfaceEventHandlers,
    getTargetDivCSSSelector,
    updateHoveredItem,
} from 'owa-calendar-surface-dategrid';
import { defaultCommandTimeout, executeCommand } from 'owa-calendar-command-executor';
import { getSurfaceStore } from '../store/publishedCalendarSurfaceStore';
import {
    CalendarMonthDayCellModel,
    clearSelectedItem,
    createOverflowIndicatorEventHandlers,
    updateSelectedDate,
    updateSelectedItem,
} from 'owa-calendar-surface';
import { isAgendaViewShown, toggleAgendaView } from 'owa-publishedcalendar-agendaview';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { logUsage } from 'owa-analytics';
import { fetchCalendarEventDetails } from 'owa-publishedcalendar-eventdetails';
import { getCalendarAgendaPaneComponent } from '../utils/registerFocusElements';

/**
 * Event handlers for items both on Day/Week and Month view
 */
export let calendarItemsEventHandlers: SurfaceEventHandlers = {
    onMouseUp: evt => evt.stopPropagation(),
    onMouseDown: onItemMouseDown,
    onMouseOver: onItemMouseOver,
    onMouseLeave: onItemMouseOut,
    onClick: onItemClick,
    onDoubleClick: onItemDoubleClick,
    onKeyDown: onItemKeyDown,
};

/**
 * Event handlers for month day cells
 */
export let monthDayCellsEventHandlers: SurfaceEventHandlers = {
    onClick: onMonthDayCellClick,
    onDoubleClick: onMonthDayCellDoubleClick,
};

/**
 * Event handlers for overflow items in month view
 */
export let monthOverflowItemsHandlers: SurfaceEventHandlers = createOverflowIndicatorEventHandlers(
    monthOverflowIndicatorOnClick
);

/**
 * Event handler onItemMouseDown
 */
function onItemMouseDown(evt: React.MouseEvent<HTMLDivElement>) {
    // This is needed to prevent the column from getting the mouse down events.
    if (!getSurfaceStore().enableDragDrop) {
        evt.stopPropagation();
    }
}

/**
 * Event handler onItemMouseOver
 */
function onItemMouseOver(this: CalendarEvent) {
    updateHoveredItem(this, getSurfaceStore());
}

/**
 * Event handler onItemMouseOut
 */
function onItemMouseOut(this: CalendarEvent) {
    updateHoveredItem(null, getSurfaceStore());
}

/**
 * Event handler onItemClick
 */
function onItemClick(this: CalendarEvent, evt: React.MouseEvent<HTMLDivElement>) {
    logUsage('onSurfaceEventClick', { currentDateRangeType: getSurfaceStore().dateRangeType });
    evt.preventDefault();
    evt.stopPropagation();
    executeCommand(() => {
        openItemDetails(this, evt.currentTarget);
    }, defaultCommandTimeout);
}

/**
 * Event handler onItemDoubleClick
 */
function onItemDoubleClick(this: CalendarEvent, evt: React.MouseEvent<HTMLDivElement>) {
    logUsage('onSurfaceEventDoubleClick', {
        currentDateRangeType: getSurfaceStore().dateRangeType,
    });
    evt.preventDefault();
    evt.stopPropagation();
    executeCommand(() => {
        // Opens ItemDetails on both single and double click
        openItemDetails(this, evt.currentTarget);
    }, defaultCommandTimeout);
}

/**
 * Called when an item has to be selected.
 * @surfaceStore The surface store
 * @selectedItem - the current selected item. This can be passed as null if one has a customTarget that is being sent
 * @target - target of the selected item
 * @customTarget - one can pass a custom selector. For instance, agenda peek uses this to select calendar items on it.
 */
export function selectItem(
    surfaceStore: CalendarSurfaceStore,
    selectedItem: CalendarEvent,
    target: HTMLDivElement,
    customTarget: string = null
) {
    let targetDiv = !customTarget ? getTargetDivCSSSelector(target) : customTarget;
    updateSelectedItem(selectedItem, targetDiv, getSurfaceStore(), customTarget);
    updateSelectedDate(selectedItem.Start, getSurfaceStore());
}

/**
 * Opens ItemDetails (CalendarReadingPane)
 */
function openItemDetails(selectedItem: CalendarEvent, target: HTMLDivElement) {
    selectItem(getSurfaceStore(), selectedItem, target);
    fetchCalendarEventDetails(selectedItem, false /*fetchSeriesMaster*/);
    updateModuleCommand('Open_ItemDetails');
}

/**
 * Event handler onMonthDayCellClick
 */
function onMonthDayCellClick(
    this: CalendarMonthDayCellModel,
    evt: React.MouseEvent<HTMLDivElement>
) {
    updateSelectedDate(this.date, getSurfaceStore());
    clearSelectedItem(getSurfaceStore());
}

/**
 * Event handler onMonthDayCellDoubleClick
 */
function onMonthDayCellDoubleClick(
    this: CalendarMonthDayCellModel,
    evt: React.MouseEvent<HTMLDivElement>
) {
    updateSelectedDate(this.date, getSurfaceStore());
    clearSelectedItem(getSurfaceStore());
}

/**
 * Month Overflow Indicator OnClick
 */
function monthOverflowIndicatorOnClick(
    surfaceItem: CalendarEvent,
    evt: React.MouseEvent<HTMLDivElement>
) {
    evt.stopPropagation();
    selectItem(getSurfaceStore(), surfaceItem, evt.currentTarget);
    updateSelectedDate(surfaceItem.Start, getSurfaceStore());
    if (!isAgendaViewShown()) {
        toggleAgendaView();
    }
    // focus on the agenda pane
    getCalendarAgendaPaneComponent().setFocus();
}

/**
 * Event handler for item key down
 */
function onItemKeyDown(this: CalendarEvent, evt: React.KeyboardEvent<HTMLDivElement>) {
    switch (evt.which) {
        case KeyCodes.enter:
            evt.preventDefault();
            openItemDetails(this, evt.currentTarget);
            break;
    }
}
